// src/main.js
import { createApp } from "vue";


import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip';
import App from "./App.vue";
import router from "./router";
import store from "@/core/services/store";
import i18n from "@/core/plugins/vue-i18n";
import MomentJalali from "@/core/plugins/moment-jalali";
import objectPath from 'object-path';
import { modalService } from '@/core/services/modal-service';
import "./registerServiceWorker";
import "@mdi/font/css/materialdesignicons.css";
import "@/core/plugins/metronic.js";
import InfiniteLoading from '@codog/vue3-infinite-loading';


const app = createApp(App);

/* -------------------------
   global properties (was Vue.prototype)
   ------------------------- */
app.config.globalProperties.$employerAttachmentUrl = process.env.VUE_APP_EMP_ATTACHMENT_URL;
app.config.globalProperties.$jobseekerImageUrl = process.env.VUE_APP_JSEEKER_IMAGE_URL;
app.config.globalProperties.$employerImageUrl = process.env.VUE_APP_EMP_IMAGE_URL;
app.config.globalProperties.$jobseekerResumeUrl = process.env.VUE_APP_JSEEKER_RESUME_URL;
app.config.globalProperties.$filesUrl = process.env.VUE_APP_FILES_URL;
app.config.globalProperties.$recaptcha = process.env.VUE_APP_RECAPTCHA;
app.config.globalProperties.$frontUrl = process.env.VUE_APP_SITE_URL;
app.config.globalProperties.$apiUrl = process.env.VUE_APP_API_URL;
app.config.globalProperties.$publisherCount = process.env.VUE_APP_PUBLISHER_COUNT;
app.config.globalProperties.$personalServer = process.env.VUE_APP_PERSONAL_SERVER === "true";
app.config.globalProperties.$textExtractorApi = process.env.VUE_APP_TEXT_EXTRACTOR_API;
app.config.globalProperties.$fooladTechnicServer = process.env.VUE_APP_FOOLAD_TECHNIC_SERVER === "true";
app.config.globalProperties.$modal = modalService;
app.config.globalProperties.$t = i18n.global.t;
app.config.globalProperties.$objectPath = objectPath;
const siteUrl = process.env.VUE_APP_SITE_URL;

/* -------------------------
   directives (resize, click-outside)
   converted to Vue 3 hooks
   ------------------------- */
import iframeResize from "iframe-resizer/js/iframeResizer";

app.directive("resize", {
  mounted(el, binding) {
    const value = binding.value || {};
    // attach listener on load
    el._soha_iframe_load_handler = () => iframeResize(value, el);
    el.addEventListener("load", el._soha_iframe_load_handler);
  },
  unmounted(el) {
    try {
      if (el.iFrameResizer && typeof el.iFrameResizer.removeListeners === "function") {
        el.iFrameResizer.removeListeners();
      }
    } catch (e) {
      // ignore
    }
    if (el._soha_iframe_load_handler) {
      el.removeEventListener("load", el._soha_iframe_load_handler);
      delete el._soha_iframe_load_handler;
    }
  },
});

// click-outside: backward-compatible: if binding.value is function -> call it,
// if it's a string (old usage), will try vnode.ctx[methodName]
app.directive("click-outside", {
  mounted(el, binding, vnode) {
    el._soha_clearDrag = function () {
      el.removeAttribute("data-dragging");
    };
    el._soha_setDrag = function () {
      el.setAttribute("data-dragging", "yes");
    };
    el._soha_onClick = function (event) {
      var dragging = el.getAttribute("data-dragging");
      if (!(el === event.target || el.contains(event.target)) && !dragging) {
        if (typeof binding.value === "function") {
          binding.value(event);
        } else if (typeof binding.value === "string" && vnode && vnode.ctx && typeof vnode.ctx[binding.value] === "function") {
          vnode.ctx[binding.value](event);
        }
      }
    };

    document.addEventListener("touchstart", el._soha_clearDrag);
    document.addEventListener("touchmove", el._soha_setDrag);
    document.addEventListener("click", el._soha_onClick);
    document.addEventListener("touchend", el._soha_onClick);
  },
  unmounted(el) {
    document.removeEventListener("touchstart", el._soha_clearDrag);
    document.removeEventListener("touchmove", el._soha_setDrag);
    document.removeEventListener("click", el._soha_onClick);
    document.removeEventListener("touchend", el._soha_onClick);
    el.removeAttribute("data-dragging");
    delete el._soha_clearDrag;
    delete el._soha_setDrag;
    delete el._soha_onClick;
  },
});

/* -------------------------
   plugins & 3rd-party (use safe app.use where possible)
   ------------------------- */
try {
  Promise.all([
    import('highcharts'),
    import('highcharts-vue')
  ]).then(([Highcharts, HighchartsVue]) => {

    Highcharts.default.setOptions({
      chart: {
        style: {
          fontFamily: 'Vazir'
        }
      },
      title: {
        style: {
          fontFamily: 'Vazir'
        }
      },
      tooltip: {
        style: {
          fontFamily: 'Vazir'
        }
      },
      legend: {
        itemStyle: {
          fontFamily: 'Vazir'
        }
      },
      xAxis: {
        labels: {
          style: {
            fontFamily: 'Vazir'
          }
        }
      },
      yAxis: {
        labels: {
          style: {
            fontFamily: 'Vazir'
          }
        }
      }
    });

    // ثبت پلاگین Vue
    app.use(HighchartsVue.default);

  }).catch(() => {/* ignore */});
} catch (e) {/* ignore */}


/* register SohaDatePicker (vue3 version) if present */
try {
  // plugin built for Vue3 (if installed)
  // if it's a component export, register it; if plugin - app.use would be needed
  // we try to register component if import works
  // note: this is an async-safe import; webpack will bundle it if present
  // (the original project used Vue.use for vue2 picker; for vue3 we register component)
  import("vue3-persian-datetime-picker").then((mod) => {
    const SohaDatePicker = mod && (mod.default || mod);
    if (SohaDatePicker) app.component("SohaDatePicker", SohaDatePicker);
  }).catch(()=>{/* ignore */});
} catch(e){ /* empty */ }

/* print plugin */
import VueHtmlToPaper from "vue-html-to-paper";
const printOption = {
  name: "_blank",
  specs: ["fullscreen=yes", "titlebar=yes"],
  styles: [
    `${siteUrl}/css/primevue/primeicons.css`,
    `${siteUrl}/css/primevue/theme.css`,
    `${siteUrl}bootstrap-4.3.1/css/bootstrap.css`,
    `${siteUrl}bootstrap-4.3.1/css/bootstrap-grade.css`,
    `${siteUrl}bootstrap-4.3.1/css/bootstrap-reboot.css`,
    `${siteUrl}bootstrap-4.3.1/css/style.vue.rtl.css`,
    `${siteUrl}/css/print.css`,
  ],
  timeout: 1000,
  autoClose: true,
  windowTitle: "",
};
try {
  app.use(VueHtmlToPaper, printOption);
} catch (e) {
  // if plugin not compatible with Vue3 keep going
}

/* PerfectScrollbar (window global like before) */
try {
  import("vue3-perfect-scrollbar").then((mod) => {
    const PerfectScrollbar = mod && (mod.default || mod);
    if (PerfectScrollbar) window.PerfectScrollbar = PerfectScrollbar;
  }).catch(()=>{/* ignore */});
} catch(e) { /* empty */ }

import ClipboardJS from "clipboard";
window.ClipboardJS = ClipboardJS;

/* -------------------------
   app.use
   ------------------------- */
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: false,
    }
  }
});
app.use(ToastService);
app.use(store);
app.use(router);
app.use(i18n);
app.use(MomentJalali);
app.use(InfiniteLoading, { /* options */ });
app.directive('tooltip', Tooltip);

/* -------------------------
   API init, axios interceptors, mixins
   ------------------------- */
import ApiService from "@/core/services/api.service";
ApiService.init(app);

import axios from "axios";
import GeneralMixin from "@/mixins/GeneralMixin";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const originalRequest = error.config;
    if (error.response && error.response.status == 503) {
      router.push({ name: "maintenance" });
      return Promise.reject(error);
    }
    if (error.response && error.response.status == 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      if (!originalRequest.headers?.jobseeker) {
        const refreshToken = localStorage.getItem("refresh_token");
        return new Promise(function (resolve, reject) {
          axios
            .post(`${process.env.VUE_APP_API_URL}api/v2/refresh`, {
              refreshToken: refreshToken,
              userTypeId: 2,
            })
            .then(({ data }) => {              
              if (!data.error) {
                const expiresIn =
                  Math.floor(new Date().getTime() / 1000) + data.expires_in;
                store.commit("setAuth", {
                  token: data.access_token,
                  expiresIn: expiresIn,
                  refreshToken: data.refresh_token,
                  remainingDay: data.remainingDaysOfActivePlan,
                });
                ApiService.setHeader("employer");
                originalRequest.headers["Authorization"] = "Bearer " + data.access_token;
                processQueue(null, data.access_token);
                resolve(axios(originalRequest));
              } else {
                GeneralMixin.methods.logoutWhenRefreshExpired();
                reject(error);
              }
            })
            .catch((err) => {
              processQueue(err, null);
              GeneralMixin.methods.logoutWhenRefreshExpired();
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      } else {
        const refreshToken = localStorage.getItem("soha_jobseeker_refresh_token");
        return new Promise(function (resolve, reject) {
          axios
            .post(`${process.env.VUE_APP_API_URL}api/v2/jobseeker/refresh`, {
              refreshToken: refreshToken,
            })
            .then(({ data }) => {
              if (!data.error) {
                
                store.commit("setJobseekerAuth", {
                    accessToken: data.access_token,
                    refreshToken: data.refresh_token,
                  });
                  ApiService.setHeader("jobseeker");
                  originalRequest.headers["Authorization"] = "Bearer " + data.access_token;
                  processQueue(null, data.access_token);
                  resolve(axios(originalRequest));
                } else {
                localStorage.removeItem("soha_jobseeker_token");
                localStorage.removeItem("soha_jobseeker_refresh_token");
                reject(error);
                document.location.reload();
              }
            })
            .catch((err) => {
              processQueue(err, null);
              localStorage.removeItem("soha_jobseeker_token");
              localStorage.removeItem("soha_jobseeker_refresh_token");
              reject(err);
              document.location.reload();
              
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }
    }

    return Promise.reject(error);
  }
);

/* -------------------------
   Register global components (preserve original names)
   ------------------------- */
   
/* --- imports --- */
import SohaTextField from "@/view/components/base/TextField.vue";
import SohaTextArea from "@/view/components/base/TextArea.vue";
import SohaSelect from "@/view/components/base/Select.vue";
import SohaMultiSelect from "@/view/components/base/MultiSelect.vue";
import SohaButton from "@/view/components/base/Button.vue";
import SohaCircularLoading from "@/view/components/base/CircularLoading.vue";
import SohaStatusLabel from "@/view/components/base/StatusLabel.vue";
import SohaButtonIcon from "@/view/components/base/ButtonIcon.vue";
import SohaCheckbox from "@/view/components/base/Checkbox.vue";
import SohaMenu from "@/view/components/base/Menu.vue";
import SohaList from "@/view/components/base/List.vue";
import SohaListItem from "@/view/components/base/ListItem.vue";
import SohaTagResume from "@/view/components/tag/TagResume.vue";
import SohaPagination from "@/view/components/base/Pagination.vue";
import SohaLimitedText from "@/view/components/base/LimitedText.vue";
import SohaLastCard from "@/view/components/base/LastCard.vue";
import SohaModal from "@/view/components/base/Modal.vue";
import SohaDeleteModal from "@/view/components/base/DeleteModal.vue";
import SohaAddNewItem from "@/view/components/base/AddNewItem.vue";
import SohaDropdown from "@/view/components/base/Dropdown.vue";
import SohaOrderSelection from "@/view/components/base/OrderSelection.vue";
import SohaSendGroupMessage from "@/view/components/resume/ResumeModalSendGroupMessage.vue";
import SohaTripleChoice from "@/view/components/base/TripleChoice.vue";
import SohaAvatar from "@/view/components/base/Avatar.vue";
import SohaEmergencyLevel from "@/view/components/base/EmergencyLevel.vue";
import SohaTextEditor from "@/view/components/base/TextEditor.vue";
import SohaSwitch from "@/view/components/base/CustomSwitch.vue";
import SohaRadioButton from "@/view/components/base/RadioButton.vue";
import SohaPageLoading from "@/view/components/base/PageLoading.vue";
import SohaNoAccess from "@/view/components/base/NoAccess.vue";
import SohaNoData from "@/view/components/base/NoData.vue";
import SohaTab from "@/view/components/base/Tab.vue";
import SohaEditResume from "@/view/components/resume/ResumeModalEdit.vue";
import SohaGuestExpertModal from "@/view/components/resume/ResumeModalGuestExpert.vue";
import SohaSendToArchiveModal from "@/view/components/resume/ResumeModalSendToArchive.vue";
import SohaFiled from "@/view/components/resume/ResumeField.vue";
import SohaDivider from "@/view/components/base/Divider.vue";
import SohaTimeline from "@/view/components/base/Timeline.vue";
import SohaTimelineItem from "@/view/components/base/TimelineItem.vue";
import SohaProgress from "@/view/components/base/Progress.vue";
import SohaCircleProgress from "@/view/components/base/CircleProgress.vue";
import SohaIntegrationJob from "@/view/components/job/JobIntegrationJobBoard.vue";
import SohaAlert from "@/view/components/base/Alert.vue";
import SohaHorizontalTimeline from "@/view/components/base/HorizontalTimeline.vue";
import SohaSideMenu from "@/view/components/base/SideMenu.vue";
import InlineSvg from 'vue-inline-svg';
import SohaDropdownItem from '@/view/components/base/DropDownItem.vue';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Loading from "./view/components/base/Loading.vue";
import SohaImageCropper from './view/components/base/ImageCropper.vue';
import SohaCircleSelector from '@/view/components/base/CircleSelector.vue';
import Skeleton from 'primevue/skeleton';
import Divider from 'primevue/divider';
import SohaStarRating from "@/view/components/base/SohaStarRating.vue";

/* --- register --- */
app.component('SohaStarRating', SohaStarRating);
app.component('InlineSvg', InlineSvg);
app.component("SohaTextField", SohaTextField);
app.component("SohaTextArea", SohaTextArea);
app.component("SohaSelect", SohaSelect);
app.component("SohaMultiSelect", SohaMultiSelect);
app.component("SohaButton", SohaButton);
app.component("SohaCircularLoading", SohaCircularLoading);
app.component("SohaStatusLabel", SohaStatusLabel);
app.component("SohaButtonIcon", SohaButtonIcon);
app.component("SohaCheckbox", SohaCheckbox);
app.component("SohaMenu", SohaMenu);
app.component("SohaList", SohaList);
app.component("SohaListItem", SohaListItem);
app.component("SohaTagResume", SohaTagResume);
app.component("SohaPagination", SohaPagination);
app.component("SohaLimitedText", SohaLimitedText);
app.component("SohaLastCard", SohaLastCard);
app.component("SohaModal", SohaModal);
app.component("SohaDeleteModal", SohaDeleteModal);
app.component("SohaAddNewItem", SohaAddNewItem);
app.component("SohaDropdown", SohaDropdown);
app.component("SohaOrderSelection", SohaOrderSelection);
app.component("SohaSendGroupMessage", SohaSendGroupMessage);
app.component("SohaTripleChoice", SohaTripleChoice);
app.component("SohaAvatar", SohaAvatar);
app.component("SohaEmergencyLevel", SohaEmergencyLevel);
app.component("SohaTextEditor", SohaTextEditor);
app.component("SohaSwitch", SohaSwitch);
app.component("SohaRadioButton", SohaRadioButton);
app.component("SohaPageLoading", SohaPageLoading);
app.component("SohaNoAccess", SohaNoAccess);
app.component("SohaNoData", SohaNoData);
app.component("SohaTab", SohaTab);
app.component("SohaEditResume", SohaEditResume);
app.component("SohaGuestExpertModal", SohaGuestExpertModal);
app.component("SohaSendToArchiveModal", SohaSendToArchiveModal);
app.component("SohaFiled", SohaFiled);
app.component("SohaDivider", SohaDivider);
app.component("SohaTimeline", SohaTimeline);
app.component("SohaTimelineItem", SohaTimelineItem);
app.component("SohaProgress", SohaProgress);
app.component("SohaCircleProgress", SohaCircleProgress);
app.component("SohaIntegrationJob", SohaIntegrationJob);
app.component("SohaAlert", SohaAlert);
app.component("SohaHorizontalTimeline", SohaHorizontalTimeline);
app.component("SohaSideMenu", SohaSideMenu);
app.component('SohaDropdownItem', SohaDropdownItem);
app.component('SohaAccordion', Accordion);
app.component('SohaAccordionTab', AccordionTab);
app.component('Loading', Loading);
app.component('SohaImageCropper', SohaImageCropper);
app.component('SohaCircleSelector', SohaCircleSelector);
app.component('SohaSkeleton', Skeleton);
app.component('SohaDivider', Divider);


app.mount("#app");